import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, RefreshCw, Plus, Search, X, Copy, CheckCircle2, Clock, XCircle,
  ExternalLink, Phone, CornerDownLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listTemplates,
  listTemplatePreviews,
  createTemplate,
  uploadTemplateMedia,
  templateStatus,
  templateStatusLabel,
  formatApiError,
  TEMPLATE_MEDIA_MAX_BYTES,
} from '../lib/campaignService';

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'approved', label: 'Approved' },
  { key: 'pending', label: 'Pending' },
  { key: 'rejected', label: 'Rejected' },
];

const CATEGORY_OPTIONS = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
const TYPE_OPTIONS = [
  { key: 'TEXT', label: 'Text' },
  { key: 'IMAGE', label: 'Image' },
  { key: 'VIDEO', label: 'Video' },
  { key: 'DOCUMENT', label: 'Document' },
];
const MEDIA_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};
const BUTTON_TYPE_OPTIONS = ['QUICK_REPLY', 'URL'];

const EMPTY_TEMPLATE_FORM = {
  element_name: '',
  category: 'UTILITY',
  template_type: 'TEXT',
  language_code: 'en',
  header: '',
  content: '',
  example: '',
  footer: '',
  buttons: [],
  mediaFile: null,
};

function templateIdOf(t) {
  return t?.id || t?.templateId || '';
}

function templateNameOf(t) {
  return t?.elementName || t?.element_name || t?.name || templateIdOf(t);
}

function templateBodyOf(t) {
  let body = t?.data || '';
  const rawMeta = t?.containerMeta;
  if (rawMeta) {
    try {
      const meta = typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta;
      body = meta?.data || body;
    } catch {
      /* fall back to raw data */
    }
  }
  return body;
}

/** Image + header + full text + footer + every button, as the recipient sees it. */
function TemplateMessage({ preview, fallbackBody }) {
  const body = preview?.body_text || fallbackBody;
  const buttons = (preview?.buttons || []).filter((b) => b?.text);
  if (!preview && !body) return null;
  return (
    <div className="template-card-message">
      {preview?.image_url && (
        <img className="campaign-preview-image" src={preview.image_url} alt="Template header" loading="lazy" />
      )}
      <div className="campaign-preview-text">
        {preview?.header && <p className="campaign-preview-header-text">{preview.header}</p>}
        {body && <p className="campaign-preview-message">{body}</p>}
        {preview?.footer && <p className="campaign-preview-footer">{preview.footer}</p>}
        {buttons.length > 0 && (
          <div className="campaign-preview-buttons">
            {buttons.map((b, i) => (
              <span key={`${b.text}-${i}`} className="campaign-preview-button" title={b.url || b.phone_number || undefined}>
                {b.type === 'URL' && <ExternalLink size={13} />}
                {b.type === 'PHONE_NUMBER' && <Phone size={13} />}
                {b.type === 'QUICK_REPLY' && <CornerDownLeft size={13} />}
                {b.text}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function templateBucket(t) {
  const s = templateStatus(t);
  if (s === 'APPROVED' || s === 'ACTIVE') return 'approved';
  if (s === 'PENDING' || s === 'SUBMITTED') return 'pending';
  if (s === 'REJECTED' || s === 'FAILED') return 'rejected';
  return 'other';
}

function formatTemplateDate(t) {
  const raw = t?.createdOn || t?.modifiedOn;
  if (!raw) return null;
  const ms = Number(raw) < 1e12 ? Number(raw) * 1000 : Number(raw);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function StatusBadge({ status }) {
  const s = String(status || '').toUpperCase();
  const approved = s === 'APPROVED' || s === 'ACTIVE';
  const pending = s === 'PENDING' || s === 'SUBMITTED';
  const rejected = s === 'REJECTED' || s === 'FAILED';
  let cls = 'template-status-badge';
  if (approved) cls += ' template-status-approved';
  else if (pending) cls += ' template-status-pending';
  else if (rejected) cls += ' template-status-rejected';
  return (
    <span className={cls}>
      {approved && <CheckCircle2 size={13} />}
      {pending && <Clock size={13} />}
      {rejected && <XCircle size={13} />}
      {templateStatusLabel(status)}
    </span>
  );
}

function CopyIdButton({ id }) {
  const [copied, setCopied] = useState(false);
  if (!id) return null;
  return (
    <button
      type="button"
      className="template-copy-id"
      onClick={() => {
        navigator.clipboard?.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      title="Copy template ID"
    >
      <Copy size={12} />
      {copied ? 'Copied' : `${id.slice(0, 8)}…`}
    </button>
  );
}

function NewTemplateModal({ onClose, onCreated, apiUrl }) {
  const [form, setForm] = useState(EMPTY_TEMPLATE_FORM);
  const [submitting, setSubmitting] = useState(false);

  const addButton = () => {
    if (form.buttons.length >= 3) return;
    setForm((f) => ({ ...f, buttons: [...f.buttons, { type: 'QUICK_REPLY', text: '', url: '' }] }));
  };
  const updateButton = (i, patch) => {
    setForm((f) => ({ ...f, buttons: f.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  };
  const removeButton = (i) => {
    setForm((f) => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));
  };

  const isMediaType = form.template_type !== 'TEXT';

  const handleSubmit = async () => {
    const name = form.element_name.trim();
    if (!name || !/^[a-z0-9_]+$/.test(name)) {
      toast.error('Template name is required — lowercase letters, numbers, underscores only');
      return;
    }
    if (!form.content.trim()) {
      toast.error('Body is required');
      return;
    }
    if (!form.example.trim()) {
      toast.error('Example is required — Meta reviews this against the body');
      return;
    }
    if (isMediaType && !form.mediaFile) {
      toast.error(`Upload a sample ${form.template_type.toLowerCase()} for this template`);
      return;
    }
    if (isMediaType && form.mediaFile.size > TEMPLATE_MEDIA_MAX_BYTES) {
      toast.error(
        `File is too large (${(form.mediaFile.size / (1024 * 1024)).toFixed(1)} MB). `
        + 'Use an image/PDF under 4 MB (ideally under 1 MB).'
      );
      return;
    }
    const buttons = form.buttons
      .map((b) => ({ type: b.type, text: b.text.trim(), url: (b.url || '').trim() }))
      .filter((b) => b.text && (b.type !== 'URL' || b.url));
    if (form.buttons.some((b) => b.type === 'URL' && !(b.url || '').trim())) {
      toast.error('Enter a link for the URL button');
      return;
    }
    setSubmitting(true);
    try {
      let example_media;
      let send_media_url;
      if (isMediaType) {
        const uploaded = await uploadTemplateMedia(apiUrl, form.mediaFile, form.template_type, name);
        example_media = uploaded?.handle_id;
        send_media_url = uploaded?.send_media_url;
        if (!example_media) throw new Error('Media upload did not return a handle');
      }
      await createTemplate(apiUrl, {
        element_name: name,
        content: form.content.trim(),
        example: form.example.trim(),
        category: form.category,
        template_type: form.template_type,
        language_code: form.language_code.trim() || 'en',
        header: !isMediaType ? (form.header.trim() || undefined) : undefined,
        footer: form.footer.trim() || undefined,
        example_media,
        send_media_url,
        buttons: buttons.length ? buttons : undefined,
      });
      toast.success('Template submitted for Meta approval');
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to submit template'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="template-modal-overlay" onClick={onClose}>
      <div className="template-modal" onClick={(e) => e.stopPropagation()}>
        <div className="template-modal-header">
          <h3>New WhatsApp Template</h3>
          <button type="button" className="form-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="template-modal-body">
          <label className="template-field">
            <span>Template name *</span>
            <input
              value={form.element_name}
              onChange={(e) => setForm((f) => ({ ...f, element_name: e.target.value }))}
              placeholder="e.g. order_confirmation"
            />
            <small>Unique, lowercase, underscores only. Cannot be changed later.</small>
          </label>

          <div className="template-field">
            <span>Category *</span>
            <div className="template-pill-group">
              {CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`template-pill-btn${form.category === c ? ' active' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, category: c }))}
                >
                  {c.charAt(0) + c.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="template-field-row">
            <div className="template-field">
              <span>Type</span>
              <div className="template-pill-group">
                {TYPE_OPTIONS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`template-pill-btn${form.template_type === t.key ? ' active' : ''}`}
                    onClick={() => setForm((f) => ({ ...f, template_type: t.key, mediaFile: null }))}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="template-field">
              <span>Language</span>
              <input
                value={form.language_code}
                onChange={(e) => setForm((f) => ({ ...f, language_code: e.target.value }))}
                placeholder="en_US"
              />
            </label>
          </div>

          {isMediaType ? (
            <label className="template-field">
              <span>Sample {form.template_type.charAt(0) + form.template_type.slice(1).toLowerCase()} *</span>
              <input
                type="file"
                accept={MEDIA_ACCEPT[form.template_type]}
                onChange={(e) => setForm((f) => ({ ...f, mediaFile: e.target.files?.[0] || null }))}
              />
              <small>
                {form.mediaFile
                  ? `${form.mediaFile.name} (${(form.mediaFile.size / 1024).toFixed(0)} KB) — keep under 4 MB`
                  : 'Max 4 MB. Prefer a small JPG under 1 MB / small PDF. Meta uses this as the header sample.'}
              </small>
            </label>
          ) : (
            <label className="template-field">
              <span>Header</span>
              <input
                value={form.header}
                onChange={(e) => setForm((f) => ({ ...f, header: e.target.value.slice(0, 60) }))}
                placeholder="Optional header (max 60 chars)"
              />
            </label>
          )}

          <label className="template-field">
            <span>Body *</span>
            <textarea
              rows={4}
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              placeholder="Your order {{1}} is confirmed and will arrive by {{2}}."
            />
            <small>Use {'{{1}}'}, {'{{2}}'}… for variables. {form.content.length}/1024</small>
          </label>

          <label className="template-field">
            <span>Example *</span>
            <textarea
              rows={3}
              value={form.example}
              onChange={(e) => setForm((f) => ({ ...f, example: e.target.value }))}
              placeholder="Same text with the placeholders filled in — Meta reviews this"
            />
          </label>

          <label className="template-field">
            <span>Footer</span>
            <input
              value={form.footer}
              onChange={(e) => setForm((f) => ({ ...f, footer: e.target.value.slice(0, 60) }))}
              placeholder="Optional footer (max 60 chars)"
            />
          </label>

          <div className="template-field">
            <span>Buttons (optional — shown under the message, max 3)</span>
            {form.buttons.map((b, i) => (
              <div key={i} className="template-button-row">
                <select
                  value={b.type}
                  onChange={(e) => updateButton(i, { type: e.target.value })}
                  className="template-button-type-select"
                >
                  {BUTTON_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt === 'QUICK_REPLY' ? 'Quick reply' : 'Link'}</option>
                  ))}
                </select>
                <input
                  value={b.text}
                  onChange={(e) => updateButton(i, { text: e.target.value })}
                  placeholder="Button text"
                />
                {b.type === 'URL' && (
                  <input
                    value={b.url || ''}
                    onChange={(e) => updateButton(i, { url: e.target.value })}
                    placeholder="https://example.com"
                  />
                )}
                <button type="button" className="form-close" onClick={() => removeButton(i)} aria-label="Remove button">
                  <X size={16} />
                </button>
              </div>
            ))}
            {form.buttons.length < 3 && (
              <button type="button" className="template-add-button-btn" onClick={addButton}>
                <Plus size={14} /> Add button
              </button>
            )}
          </div>

          <button
            type="button"
            className="campaign-primary-btn template-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            <Plus size={16} /> {submitting ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TemplatesTab({ apiUrl }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // template id -> full preview (image, footer, buttons); loaded alongside the list.
  const [previews, setPreviews] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    listTemplatePreviews(apiUrl)
      .then((list) => setPreviews(Object.fromEntries(list.map((p) => [p.template_id, p]))))
      .catch((err) => console.error('Template previews failed', err));
    try {
      const raw = await listTemplates(apiUrl);
      setTemplates(raw);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load WhatsApp templates from Gupshup');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (filter !== 'all' && templateBucket(t) !== filter) return false;
      if (q && !templateNameOf(t).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [templates, filter, search]);

  return (
    <div className="campaigns-section">
      <div className="campaigns-header">
        <h2><FileText size={24} /> WhatsApp Templates</h2>
        <div className="campaigns-actions">
          <div className="campaign-search">
            <Search size={16} />
            <input
              type="search"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="button" className="refresh-btn" onClick={load}>
            <RefreshCw size={18} />
          </button>
          <button type="button" className="campaign-primary-btn" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> New Template
          </button>
        </div>
      </div>

      <div className="template-filter-tabs">
        {FILTER_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`template-filter-tab${filter === t.key ? ' active' : ''}`}
            onClick={() => setFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">Loading templates…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>No templates found</p>
          <span>Submit a new WhatsApp template to get started.</span>
        </div>
      ) : (
        <div className="template-list">
          {filtered.map((t) => {
            const id = templateIdOf(t);
            const date = formatTemplateDate(t);
            const body = templateBodyOf(t);
            return (
              <div key={id} className="template-card">
                <div className="template-card-top">
                  <h3>{templateNameOf(t)}</h3>
                  <StatusBadge status={t.status} />
                  <span className="template-tag">{t.category || 'UTILITY'}</span>
                  <span className="template-tag">{t.templateType || 'TEXT'} · {t.languageCode || 'en'}</span>
                </div>
                <TemplateMessage preview={previews[id]} fallbackBody={body} />
                <div className="template-card-footer">
                  <CopyIdButton id={id} />
                  {date && <span>{date}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <NewTemplateModal
          apiUrl={apiUrl}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}
